import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { usePrintSettings } from '../../context/PrintSettingsContext';
import { challanPrintOverrideCss } from '../../utils/challanPrintLayout';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes, faBoxOpen, faPencilAlt, faSave, faSpinner, faEnvelope, faComment } from '@fortawesome/free-solid-svg-icons';
import { useChannelSettings } from '../../context/ChannelSettingsContext';
import SupervisorHistoryButton from '../common/SupervisorHistoryButton';
import deliveryOrderService from '../../services/deliveryOrderService';
import { USER_ROLES } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import { coilLineHtml } from '../../utils/printHelpers';
import { escapeHtml } from '../../utils/escapeHtml';
import { companyHeader } from '../../utils/company';

const InfoField = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {label}
      </span>
      <span className="text-[13px] font-medium mt-0.5 leading-tight" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
        {value}
      </span>
    </div>
  );
};

const FREIGHT_TYPE_LABELS = {
  freight_chargeable: 'Freight Chargeable',
  free_delivery_freight_to_pay: 'Freight To Pay',
  ex_warehouse: 'Ex Warehouse',
  free_delivery_by_supplier: 'Free by Supplier',
  free_delivery_paid_by_company: 'Freight Paid by Company',
};


const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return d;
  }
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ChallanSuccessModal = ({ isOpen, onClose, data, onSendNotification, onMarkChallanPrinted, userRole, onEditSuccess }) => {
  const { deliveryChallanPaperSize: challanPaperSize } = usePrintSettings();
  const { t } = useTranslation();
  const [emailLoading, setEmailLoading] = useState(false);
  const { emailEnabled, whatsappEnabled } = useChannelSettings();
  const [editMode, setEditMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Edit form state
  const [challanDate, setChallanDate] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverContact, setDriverContact] = useState('');
  const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
  const [finalTruckWeight, setFinalTruckWeight] = useState('');
  const [loadingCostPerKg, setLoadingCostPerKg] = useState('');
  const [expenses, setExpenses] = useState('');
  const [expenseRemark, setExpenseRemark] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountRemark, setDiscountRemark] = useState('');
  const [roundingOff, setRoundingOff] = useState('');
  // True once the operator edits the rounding box; a pre-filled stored value is
  // not "typed" and is not re-sent (policy P3-3).
  const [roundingTouched, setRoundingTouched] = useState(false);
  const [remark, setRemark] = useState('');
  const [orderRemark, setOrderRemark] = useState('');
  const [vehicleRemark, setVehicleRemark] = useState('');
  const [itemsUpdate, setItemsUpdate] = useState([]);

  const challanPrinted = !!data?.challan_printed;
  const canEdit = challanPrinted
    ? [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(userRole)
    : [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.OPERATIONS_MANAGER].includes(userRole);

  const initEditForm = useCallback(() => {
    if (!data) return;
    setChallanDate(data.challan_date || todayIso());
    setVehicleNumber(data.vehicle_number || '');
    setDriverContact(data.driver_contact || '');
    setEmptyTruckWeight(data.empty_truck_weight ?? '');
    setFinalTruckWeight(data.final_truck_weight ?? '');
    setLoadingCostPerKg(data.loading_cost_per_kg ?? '');
    setExpenses(data.expenses ?? '');
    setExpenseRemark(data.expense_remark || '');
    setDiscount(data.discount ?? '');
    setDiscountRemark(data.discount_remark || '');
    setRoundingOff(data.rounding_off ?? '');
    setRoundingTouched(false);
    setRemark(data.remark || '');
    setOrderRemark(data.order_remark || '');
    setVehicleRemark(data.vehicle_remark || '');
    setItemsUpdate(
      Array.isArray(data.items)
        ? data.items.map(it => ({
          id: it.id,
          product_name: it.product_name,
          size_name: it.size_name,
          unit: it.unit,
          actual_quantity: it.actual_quantity ?? 0,
          actual_weight: it.actual_weight ?? 0,
          actual_pieces: it.actual_pieces ?? it.quantity_in_pieces ?? 0,
          per_piece_weight: it.per_piece_weight ?? it.standard_weight ?? 0,
          price: it.price ?? 0,
          remark: it.remark || it.order_item_remark || '',
          length_note: it.coil_tracked ? (it.length_note ?? '') : '',
          coil_tracked: Boolean(it.coil_tracked),
        }))
        : []
    );
  }, [data]);

  useEffect(() => {
    if (!editMode) return;
    initEditForm();
  }, [editMode, initEditForm]);

  const liveTotals = useMemo(() => {
    if (!data) return null;
    const liveExp = Number(expenses) || 0;
    const liveDis = Number(discount) || 0;
    const liveRound = Number(roundingOff) || 0;

    // Recompute calculated truck weight live
    const liveCalcWeight = Math.max(0, (Number(finalTruckWeight) || 0) - (Number(emptyTruckWeight) || 0));

    // Recompute total item weight live
    const totalItemWeight = itemsUpdate.reduce((s, it) => s + (Number(it.actual_weight) || 0), 0);
    const loadingWeight = totalItemWeight > 0 ? totalItemWeight : liveCalcWeight;

    // Recompute loading cost live from rate × live weight
    const liveLoadingRate = Number(loadingCostPerKg) || 0;
    const liveLoading = loadingWeight > 0 ? (liveLoadingRate * loadingWeight) : (Number(data.total_loading_cost) || 0);

    // Derive loading GST rate from original data (e.g. 18%)
    const origLoading = Number(data.total_loading_cost) || 0;
    const origLoadingGst = Number(data.loading_cost_gst_amount) || 0;
    const loadingGstRate = origLoading > 0 ? origLoadingGst / origLoading : 0;
    const liveLoadingGst = liveLoading * loadingGstRate;

    const freight = Number(data.freight) || 0;
    const freightGst = Number(data.freight_gst_amount) || 0;
    const expenseGst = Number(data.expense_gst_amount) || 0;
    const discountGst = Number(data.discount_gst_amount) || 0;

    const fType = data.freight_type || '';
    const freightForTotal = fType === 'freight_chargeable' ? freight : fType === 'free_delivery_freight_to_pay' ? -freight : 0;
    const freightGstForTotal = fType === 'freight_chargeable' ? freightGst : 0;

    const liveItemAmounts = itemsUpdate.map((it, idx) => {
      const gstPct = Number(data.items?.[idx]?.gst_percentage) || 0;
      const qty = Number(it.actual_quantity) || 0;
      const price = Number(it.price) || 0;
      const amount = qty * price;
      return { amount, gstAmount: amount * gstPct / 100 };
    });

    const liveSubtotal = liveItemAmounts.reduce((s, a) => s + a.amount, 0);
    const liveItemGst = liveItemAmounts.reduce((s, a) => s + a.gstAmount, 0);

    const totalWithoutGst = liveSubtotal + liveLoading + freightForTotal + liveExp - liveDis;

    // If GST is included, don't add any additional GST amount to the total
    const isGstIncluded = !!data.gst_included;
    const combinedGst = isGstIncluded
      ? 0
      : (liveItemGst + liveLoadingGst + freightGstForTotal + expenseGst - discountGst);

    const totalWithGst = totalWithoutGst + combinedGst;
    const finalTotal = totalWithGst + liveRound;

    return { liveSubtotal, liveItemAmounts, loadingWeight, liveLoading, liveCalcWeight, liveExp, liveDis, liveRound, totalWithoutGst, combinedGst, totalWithGst, finalTotal };
  }, [data, itemsUpdate, expenses, discount, roundingOff, loadingCostPerKg, finalTruckWeight, emptyTruckWeight]);

  // Auto-update Final Truck Weight based on items and empty weight
  useEffect(() => {
    if (!editMode || !itemsUpdate.length) return;
    const totalItemWeight = itemsUpdate.reduce((sum, item) => sum + (Number(item.actual_weight) || 0), 0);
    const emptyWeight = Number(emptyTruckWeight) || 0;
    if (totalItemWeight > 0) {
      setFinalTruckWeight(Math.round(emptyWeight + totalItemWeight).toString());
    }
  }, [itemsUpdate, emptyTruckWeight, editMode]);

  // Auto rounding-off: round total-with-GST to nearest integer (≤0.5 → down, >0.5 → up)
  useEffect(() => {
    if (!editMode || !liveTotals) return;
    const twg = liveTotals.totalWithGst;
    const fraction = twg - Math.floor(twg);
    const autoRound = fraction <= 0.5 ? -fraction : (1 - fraction);
    setRoundingOff(parseFloat(autoRound.toFixed(2)));
  }, [liveTotals, editMode]);

  const handleEnterEdit = () => {
    if (!canEdit) return;
    initEditForm();
    setEditMode(true);
  };

  const handleCancelEdit = () => setEditMode(false);

  const handleSave = async () => {
    if (!data || !canEdit) return;
    const subOrderId = data.id || data.sub_order_id;
    if (!subOrderId) return;
    setSaveLoading(true);
    try {
      const payload = {
        challan_date: challanDate,
        vehicle_number: vehicleNumber,
        driver_contact: driverContact,
        empty_truck_weight: emptyTruckWeight !== '' ? Number(emptyTruckWeight) : 0,
        final_truck_weight: finalTruckWeight !== '' ? Number(finalTruckWeight) : 0,
        loading_cost_per_kg: loadingCostPerKg !== '' ? Number(loadingCostPerKg) : 0,
        expenses: expenses !== '' ? Number(expenses) : 0,
        expense_remark: expenseRemark,
        discount: discount !== '' ? Number(discount) : 0,
        discount_remark: discountRemark,
        // Sent only when typed; otherwise the backend re-rounds the new total (P3-3).
        // (typed → value; typed then cleared → null = "round for me"; untouched → omitted)
        rounding_off: roundingTouched ? (roundingOff !== '' ? Number(roundingOff) : null) : undefined,
        remark,
        order_remark: orderRemark,
        vehicle_remark: vehicleRemark,
        items_update: itemsUpdate.map(it => ({
          id: it.id,
          actual_quantity: Number(it.actual_quantity) || 0,
          actual_weight: Number(it.actual_weight) || 0,
          actual_pieces: Number(it.actual_pieces) || 0,
          per_piece_weight: Number(it.per_piece_weight) || 0,
          price: Number(it.price) || 0,
          remark: it.remark || '',
          length_note: it.coil_tracked
            ? (it.length_note || null)
            : null,
        })),
      };
      const result = await deliveryOrderService.editChallan(subOrderId, payload);
      if (result.success) {
        setEditMode(false);
        if (onEditSuccess) onEditSuccess(result.data);
      } else {
        alert(result.error || 'Failed to update challan');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const updateItem = (idx, field, value) => {
    setItemsUpdate(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  // Round-to-nearest-integer rounding-off: ≤0.5 fraction rounds down, >0.5 rounds up.
  const computeRoundingOff = (total) => {
    const t = Number(total) || 0;
    const fraction = t - Math.floor(t);
    return parseFloat((fraction <= 0.5 ? -fraction : (1 - fraction)).toFixed(2));
  };

  const handleSendNotification = async (type) => {
    if (!onSendNotification) return;
    setEmailLoading(true);
    try {
      // Was fire-and-forget: a failed send (bad contact/channel down/500) resolved
      // silently and looked identical to success. Surface the outcome (alert, the
      // same feedback channel this modal uses for save/print).
      const res = await onSendNotification(type);
      if (res && res.success === false) {
        alert(res.error || t('deliveryOrdersModule.forms.challan.sendFailed', 'Could not send. Please try again.'));
      } else if (res && res.success === true) {
        alert(t('deliveryOrdersModule.forms.challan.sendSuccess', 'Sent successfully.'));
      }
      // A handler that returns nothing (older callers) stays silent rather than
      // falsely claiming success.
    } catch (e) {
      alert(e?.message || t('deliveryOrdersModule.forms.challan.sendFailed', 'Could not send. Please try again.'));
    } finally {
      setEmailLoading(false);
    }
  };
  const headerTitle = t('deliveryOrdersModule.forms.challan.title');


  const totals = useMemo(() => {
    if (!data) return { totalWithoutGst: 0, combinedGst: 0, finalTotalWithGst: 0 };

    // Filter items: for direct flow only require amount > 0; for warehouse flow require qty+pieces+amount
    const isDirectFlow = !!data.is_direct_flow;
    const items = Array.isArray(data.items)
      ? data.items.filter(it => {
        if (!it || Object.keys(it).length === 0) return false;
        const amount = Number(it.amount || 0);
        if (isDirectFlow) return amount !== 0;
        const qty = Number(it.actual_quantity || 0);
        const pieces = Number(it.actual_pieces || 0);
        return qty !== 0 && pieces !== 0 && amount !== 0;
      })
      : [];

    const totalItemAmount = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    const totalGst = items.reduce((sum, it) => sum + (Number(it.gst_amount) || 0), 0);

    const loading = Number(data.total_loading_cost) || 0;
    const loadingGst = Number(data.loading_cost_gst_amount) || 0;
    const freight = Number(data.freight) || 0;
    const freightGst = Number(data.freight_gst_amount) || 0;

    // Financial fields
    const expenses = Number(data.expenses) || 0;
    const expenseGst = Number(data.expense_gst_amount) || 0;
    const discount = Number(data.discount) || 0;
    const discountGst = Number(data.discount_gst_amount) || 0;

    // Freight billing effect based on freight_type:
    // freight_chargeable → add freight + freight GST
    // free_delivery_freight_to_pay → subtract freight, no freight GST
    // others → display only, no billing effect
    const fType = data.freight_type || '';
    const isFreightChargeable = fType === 'freight_chargeable';
    const isFreightToPay = fType === 'free_delivery_freight_to_pay';
    const freightForTotal = isFreightChargeable ? freight : (isFreightToPay ? -freight : 0);
    const freightGstForTotal = isFreightChargeable ? freightGst : 0;

    const totalWithoutGst = totalItemAmount + loading + freightForTotal + expenses - discount;

    // If GST is included, don't add any additional GST amount to the total
    const isGstIncluded = !!data.gst_included;
    const combinedGst = isGstIncluded
      ? 0
      : (totalGst + loadingGst + freightGstForTotal + expenseGst - discountGst);

    const finalTotalWithGst = totalWithoutGst + combinedGst;

    const totalWeight = Number(data.total_delivered_quantity) || items.reduce((sum, it) => sum + (Number(it.actual_weight || it.actual_quantity) || 0), 0);

    return { totalWithoutGst, combinedGst, finalTotalWithGst, totalItemAmount, totalWeight };
  }, [data]);

  const { totalWithoutGst, combinedGst, finalTotalWithGst, totalItemAmount: filteredSubtotal } = totals;

  const printableHtml = useMemo(() => {
    if (!data) return '';
    // HTML-escaped: this string is written into the print window (P1-2).
    const safe = (v) => escapeHtml(v);
    const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
    const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
    const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const isDirectFlow = !!data.is_direct_flow;

    const dueDateCSM = data.due_date ? fmt(data.due_date) : null;

    const items = Array.isArray(data.items)
      ? data.items.filter(it => {
          if (!it || Object.keys(it).length === 0) return false;
          const amount = Number(it.amount || 0);
          if (isDirectFlow) return amount !== 0;
          const qty = Number(it.actual_quantity || 0);
          const pieces = Number(it.actual_pieces || 0);
          return qty !== 0 && pieces !== 0 && amount !== 0;
        })
      : [];

    const hasCoilItem = items.some(it => Boolean(it?.coil_tracked));

    // HSN Summary Calculation
    const hsnSummaryMap = items.reduce((acc, it) => {
      const hsn = safe(it.hsn_code) || 'N/A';
      if (!acc[hsn]) {
        acc[hsn] = { qty: 0, amount: 0, gst_amount: 0, gst: Number(it.gst_percentage) || 0, unit: safe(it.unit) };
      }
      // Sum the SAME actual weight shown in the line "Weight" column and used
      // for Total Quantity and the amounts (amount = rate × weight). Previously
      // this summed actual_quantity, which differs from actual_weight per line,
      // making the HSN-summary total overstate the challan's Total Quantity.
      const qtyForHsn = isDirectFlow
        ? Math.ceil(Number(it.actual_weight) || Number(it.quantity) || 0)
        : Math.ceil(Number(it.actual_weight) || 0);
      acc[hsn].qty += qtyForHsn;
      acc[hsn].amount += Number(it.amount) || 0;
      acc[hsn].gst_amount += Number(it.gst_amount) || 0;
      return acc;
    }, {});

    const itemsRows = items.map((it, idx) => {
      const actualPieces = it.actual_pieces || '-';
      const isMeter = safe(it.unit).toLowerCase() === 'm';
      const displayQtyVal = isDirectFlow
        ? Math.ceil(parseFloat(it.quantity || 0))
        : Math.ceil(parseFloat(it.actual_quantity || 0));
      const displayWeightVal = isDirectFlow
        ? Math.ceil(parseFloat(it.actual_weight || 0) || parseFloat(it.quantity || 0))
        : Math.ceil(parseFloat(it.actual_weight || 0));
      const actualQty = isMeter ? `${displayQtyVal} m` : `${displayQtyVal} kg`;
      const itemRemark = safe(it.remark || it.order_item_remark || '').trim();
      const lengthNote = Boolean(it?.coil_tracked)
        ? safe(it.length_note).trim()
        : '';

      return `
      <tr>
        <td>${idx + 1}</td>
        <td>
          <div style="font-weight: 700; font-size: 9px;">${safe(it.size_name || it.product_name)}</div>
          <span style="font-size: 6px; color: #666;">(${safe(it.product_name)})</span>
          ${coilLineHtml(it)}
        </td>
        <td>${safe(it.hsn_code || '-')}</td>
        <td class="text-right">${actualPieces}</td>
        <td class="text-right">${actualQty}</td>
        <td class="text-right">${displayWeightVal} kg</td>
        <td class="text-right">₹ ${Number(it.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td class="text-right">₹ ${Number(it.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        ${hasCoilItem ? `<td style="width: 55px; max-width: 55px; white-space: normal; overflow-wrap: anywhere; word-break: break-word;">${lengthNote || '-'}</td>` : ''}
        <td>${itemRemark || '-'}</td>
      </tr>`;
    }).join('\n');

    const hsnRows = Object.entries(hsnSummaryMap).map(([hsn, data]) => {
      return `
      <tr>
        <td>${hsn}</td>
        <td>${Math.ceil(data.qty)} ${data.unit}</td>
        <td>₹ ${data.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td>${data.gst}%</td>
        <td>₹ ${data.gst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td>₹ ${(data.amount + data.gst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>`;
    }).join('\n');

    const totalItemAmount = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    const totalWeight = isDirectFlow
      ? items.reduce((sum, it) => sum + (Number(it.actual_weight) || Number(it.quantity) || 0), 0)
      : (Number(data.total_delivered_quantity) || items.reduce((sum, it) => sum + (Number(it.actual_weight || it.actual_quantity) || 0), 0));
    const loading = Number(data.total_loading_cost) || 0;
    const freight = Number(data.freight) || 0;
    const freightGstAmt = Number(data.freight_gst_amount) || 0;

    // Freight type billing logic
    const freightTypePrint = data.freight_type || '';
    const isFreightChargeablePrint = freightTypePrint === 'freight_chargeable';
    const isFreightToPayPrint = freightTypePrint === 'free_delivery_freight_to_pay';
    const showFreightRowPrint = !!freightTypePrint;
    const freightPricingTypePrint = data.freight_pricing_type || '';
    const freightLabelPrint = isFreightChargeablePrint
      ? `Freight Chargeable${freightPricingTypePrint === 'per_kg' ? ' (Per KG)' : freightPricingTypePrint === 'fixed' ? ' (Fixed)' : ''}`
      : isFreightToPayPrint ? 'Freight To Pay'
        : freightTypePrint === 'ex_warehouse' ? 'Ex Warehouse'
          : freightTypePrint === 'free_delivery_by_supplier' ? 'Free by Supplier'
            : freightTypePrint === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
              : 'Freight';
    const freightValuePrint = isFreightToPayPrint
      ? `- ₹ ${freight.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
      : isFreightChargeablePrint
        ? `₹ ${freight.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        : `₹ ${freight.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    const freightColorPrint = isFreightToPayPrint ? 'color: #059669;' : (!isFreightChargeablePrint ? 'color: #9ca3af;' : '');

    // Financial fields
    const expenses = Number(data.expenses) || 0;
    const expenseRemark = safe(data.expense_remark);
    const discount = Number(data.discount) || 0;
    const discountRemark = safe(data.discount_remark);

    const companyDetailsHtml = [
      process.env.REACT_APP_COMPANY_ADDRESS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '',
      (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '',
      process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : '',
    ].filter(Boolean).join('');

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Delivery Challan - ${safe(data.order_number)}</title>
  <style>
    @page { 
      size: A5;
      margin: 8mm;
    }
    * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
    body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
    .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
    .print-container { max-width: 148mm; margin: 0 auto; padding: 8mm; }
    .company-header { text-align: center; font-size: 16px; font-weight: 800; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 1px; color: #000; }
    .title { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; margin-bottom: 8px; font-size: 8px; }
    .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 1px; }
    .header-label { font-weight: 600; min-width: 50px; }
    
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 7px; border: 1px solid #ddd; }
    th, td { padding: 2px 3px; text-align: left; border: 1px solid #ddd; }
    th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 6px; letter-spacing: 0.3px; }
    .text-right { text-align: right; }
    
    .footer-section { display: grid; grid-template-columns: 1.5fr 1fr; gap: 8px; margin-top: 6px; align-items: flex-start; font-size: 7px; }
    .total-qty-row { font-size: 8px; font-weight: 700; margin-bottom: 4px; }
    .remarks-box { font-size: 7px; margin-top: 4px; color: #666; }
    .remarks-label { font-weight: 600; color: #444; margin-bottom: 2px; display: block; }
    
    .hsn-summary-title { font-size: 8px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; color: #444; }
    .hsn-table td, .hsn-table th { padding: 2px 3px; font-size: 7px; }
    .hsn-table .total-row td { border-top: 2px solid #555; font-weight: 700; background: #fcfcfc; }
    
    .summary-table { width: 100%; border: none; }
    .summary-table td { padding: 2px 0; border: none; font-size: 8px; }
    .summary-table .label { color: #555; }
    .summary-table .value { text-align: right; font-weight: 600; }
    .summary-table .divider { border-top: 1px solid #ddd; margin: 4px 0; height: 1px; }
    .summary-table .intermediate-total td { font-weight: 700; border-top: 1px solid #ddd; padding-top: 4px; }
    .summary-table .gst-row td { color: #444; font-size: 8px; padding: 2px 0; }
    .summary-table .net-total-row td { border-top: 2px solid #333; font-weight: 800; font-size: 9px; padding-top: 4px; }
    
    @media print { 
      .no-print { display: none !important; } 
      .print-container { padding: 0; }
      body { font-size: 9px; }
    }
  ${challanPrintOverrideCss(challanPaperSize, 'portrait')}
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print Challan</button></div>
  <div class="print-container">
    ${!data.gst_included ? `
      <div class="company-header">${safe(companyHeader(data.warehouse_name))}</div>
      ${companyDetailsHtml}
      <div class="title" style="margin-bottom: 12px;">Delivery Challan</div>
    ` : `
      <div class="title" style="margin-bottom: 12px;">Delivery Challan</div>
    `}
    
    <div class="header-grid">
      <div class="header-field" style="grid-column: 1 / -1;"><span class="header-label">Customer:</span> <span>${safe(data.customer_name)}</span></div>
      <div class="header-field" style="grid-column: 1 / -1;"><span class="header-label">Customer Address:</span> <span>${safe(data.customer_address)}</span></div>
      <div class="header-field"><span class="header-label">Date:</span> <span>${fmt(data.challan_date || data.date)}</span></div>
      ${(data.order_price_list_date || data.price_list_date) ? `<div class="header-field"><span class="header-label">Price List Date:</span> <span>${fmt(data.order_price_list_date || data.price_list_date)}</span></div>` : ''}
      ${(data.order_created_at || data.created_at) ? `<div class="header-field"><span class="header-label">Booking Date:</span> <span>${fmt(data.order_created_at || data.created_at)}</span></div>` : ''}
      <div class="header-field"><span class="header-label">Challan Serial:</span> <span>${safe(data.challan_number)}</span></div>
      ${data.payment_terms ? `<div class="header-field"><span class="header-label">Payment Terms:</span> <span>${safe(data.payment_terms)}</span></div>` : ''}
      ${dueDateCSM ? `<div class="header-field"><span class="header-label">Due Date:</span> <span>${dueDateCSM}</span></div>` : ''}
      
      <div class="header-field" style="grid-column: 1 / -1;"><span class="header-label">Delivery Address:</span> <span>${safe(data.location_address)}</span></div>

      
      <div class="header-field"><span class="header-label">Time Created:</span> <span>${fmtTime(data.created_at || data.date)}</span></div>
      <div class="header-field"><span class="header-label">Time Print:</span> <span>${nowTime}</span></div>
      
      <div class="header-field"><span class="header-label">Truck Number:</span> <span>${safe(data.vehicle_number)}</span></div>
      <div class="header-field"><span class="header-label">Driver Contact:</span> <span>${safe(data.driver_contact)}</span></div>
      
      <div class="header-field"><span class="header-label">Empty Weight:</span> <span>${safe(data.empty_truck_weight)} KG</span></div>
      <div class="header-field"><span class="header-label">Final Weight:</span> <span>${safe(data.final_truck_weight)} KG</span></div>

      ${data.challan_generated_by_name ? `<div class="header-field"><span class="header-label">Generated By:</span> <span>${safe(data.challan_generated_by_name)}</span></div>` : ''}
      ${data.sdo_created_by_name ? `<div class="header-field"><span class="header-label">Order Created By:</span> <span>${safe(data.sdo_created_by_name)}</span></div>` : ''}
      ${data.do_created_by_name ? `<div class="header-field"><span class="header-label">Delivery Order Created By:</span> <span>${safe(data.do_created_by_name)}</span></div>` : ''}
      ${(Array.isArray(data.supervisor_history) && data.supervisor_history.length > 0) ? `<div class="header-field" style="grid-column: 1 / -1;"><span class="header-label">Supervisors:</span> <span>${data.supervisor_history.map(s => safe(s.supervisor_name)).filter(Boolean).join(', ')}</span></div>` : ''}
      ${data.order_remark ? `<div class="header-field" style="grid-column: 1 / -1;"><span class="header-label">Order Remark:</span> <span style="color: #92400e; background: #fefce8; border: 1px solid #fde68a; padding: 1px 4px; border-radius: 3px;">${safe(data.order_remark)}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width: 25px;">Sr.</th>
          <th>Description</th>
          <th style="width: 45px;">HSN</th>
          <th class="text-right" style="width: 35px;">Pcs</th>
          <th class="text-right" style="width: 50px;">Qty</th>
          <th class="text-right" style="width: 50px;">Weight</th>
          <th class="text-right" style="width: 60px;">Rate</th>
          <th class="text-right" style="width: 70px;">Amount</th>
          ${hasCoilItem ? '<th style="width: 55px;">Length</th>' : ''}
          <th style="width: 50px;">Remarks</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="footer-section">
      <div class="left-col">
        <div class="total-qty-row">Total Quantity: ${safe(Math.ceil(totalWeight))} KG</div>
        
        <div class="hsn-summary">
          <div class="hsn-summary-title">HSN Summary</div>
          <table class="hsn-table">
            <thead>
              <tr>
                <th>HSN Code</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>GST %</th>
                <th>GST Amount</th>
                <th>Total Inc GST</th>
              </tr>
            </thead>
            <tbody>
              ${hsnRows}
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-col">
        <table class="summary-table">
          <tr>
            <td class="label">Subtotal</td>
            <td class="value">₹ ${totalItemAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
          <tr>
            <td class="label">Handling Charges</td>
            <td class="value">₹ ${loading.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
          ${showFreightRowPrint ? `
          <tr>
            <td class="label" style="${freightColorPrint}">${freightLabelPrint}</td>
            <td class="value" style="${freightColorPrint}">${freightValuePrint}</td>
          </tr>
          ${isFreightChargeablePrint && freightGstAmt > 0 ? `
          <tr>
            <td class="label" style="padding-left: 8px; font-size: 6px; color: #6b7280;">Freight GST</td>
            <td class="value" style="font-size: 6px; color: #6b7280;">₹ ${freightGstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}` : ''}

          ${expenses > 0 ? `
          <tr>
            <td class="label">Expenses ${expenseRemark ? `(${expenseRemark})` : ''}</td>
            <td class="value">₹ ${expenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          
          ${discount > 0 ? `
          <tr>
            <td class="label" style="color: #059669;">Discount ${discountRemark ? `(${discountRemark})` : ''}</td>
            <td class="value" style="color: #059669;">- ₹ ${discount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}

          <tr class="intermediate-total">
            <td class="label">Total Without GST</td>
            <td class="value">₹ ${totalWithoutGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>

          <tr class="gst-row">
            <td class="label">GST @ 18%</td>
            <td class="value">₹ ${combinedGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
          
          <tr class="net-total-row">
            <td class="label">Total Amount (inc. GST)</td>
            <td class="value">₹ ${finalTotalWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
          ${(() => {
        const printRoundOff = computeRoundingOff(finalTotalWithGst);
        return printRoundOff !== 0 ? `
          <tr>
            <td class="label" style="color: #6b7280;">Rounding Off</td>
            <td class="value" style="color: #6b7280;">₹ ${printRoundOff.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : '';
      })()}
          <tr style="border-top: 2px solid #1e3a5f; background: #f0f4ff;">
            <td class="label" style="font-weight: 800; font-size: 10px; color: #1e3a5f;">Grand Total</td>
            <td class="value" style="font-weight: 800; font-size: 10px; color: #1e3a5f;">₹ ${Math.round(finalTotalWithGst).toLocaleString('en-IN')}</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;

    return html;
  }, [data, totalWithoutGst, combinedGst, finalTotalWithGst, challanPaperSize]);

  const handleDownload = async () => {
    if (!printableHtml) return;

    const w = window.open('', '_blank');
    if (!w) {
      alert(t('common.messages.unableToOpenPrintWindow'));
      return;
    }
    try {
      w.document.open();
      w.document.write(printableHtml);
      w.document.close();
      try { w.document.title = headerTitle || ''; } catch (e) { }
      setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } }, 300);
    } catch (err) {
      console.error('Print window error', err);
      alert(t('common.messages.unableToPreparePrintContent'));
    }
    if (onMarkChallanPrinted) onMarkChallanPrinted();
  };

  if (!isOpen || !data) return null;

  const lt = liveTotals;
  const displayLoading = editMode ? (lt?.liveLoading ?? 0) : Number(data.total_loading_cost || 0);
  const displayCalcWeight = editMode ? (lt?.liveCalcWeight ?? 0) : Number(data.calculated_truck_weight || 0);
  const displaySubtotal = editMode ? (lt?.liveSubtotal ?? 0) : filteredSubtotal;
  const displayExpenses = editMode ? (lt?.liveExp ?? 0) : Number(data.expenses || 0);
  const displayDiscount = editMode ? (lt?.liveDis ?? 0) : Number(data.discount || 0);
  const displayRoundingOff = editMode ? (lt?.liveRound ?? 0) : computeRoundingOff(finalTotalWithGst);
  const displayTotalWithoutGst = editMode ? (lt?.totalWithoutGst ?? 0) : totalWithoutGst;
  const displayCombinedGst = editMode ? (lt?.combinedGst ?? 0) : combinedGst;
  const displayTotalWithGst = editMode ? (lt?.totalWithGst ?? 0) : finalTotalWithGst;
  const displayFinalTotal = editMode ? (lt?.finalTotal ?? 0) : Math.round(finalTotalWithGst);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
      role="dialog"
      aria-modal="true"
    >
      <style>{`
        .csm-no-spin::-webkit-inner-spin-button,
        .csm-no-spin::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .csm-no-spin { -moz-appearance: textfield; appearance: textfield; }
      `}</style>
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{headerTitle}</h2>
            <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {data.challan_number ? `Challan Generated Successfully: ${data.challan_number}` : 'Challan generated successfully.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!editMode && canEdit && (
              <button
                onClick={handleEnterEdit}
                className="p-3 rounded-xl transition-all hover:scale-105 active:scale-95"
                title={t('common.actions.editChallan')}
                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08) }}
              >
                <FontAwesomeIcon icon={faPencilAlt} className="text-lg" />
              </button>
            )}
            {!editMode && (
              <button
                onClick={handleDownload}
                className="p-3 rounded-xl transition-all hover:scale-105 active:scale-95"
                title={t('common.actions.printChallan')}
                style={{ color: UI_COLORS.BRAND.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.08) }}
              >
                <FontAwesomeIcon icon={faPrint} className="text-lg" />
              </button>
            )}
            <button
              onClick={editMode ? handleCancelEdit : onClose}
              className="p-3 rounded-xl transition-all hover:scale-105 active:scale-95"
              style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto grow space-y-8">

          {/* Primary Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t('deliveryOrdersModule.view.fields.subOrderNumber')}</div>
              <div className="px-4 py-3 rounded-xl border-2 font-semibold text-gray-800 bg-gray-50 border-gray-100">{data.sub_order_number}</div>
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t('deliveryOrdersModule.view.fields.customerName')}</div>
              <div className="px-4 py-3 rounded-xl border-2 font-semibold text-gray-800 bg-gray-50 border-gray-100 truncate" title={data.customer_name}>{data.customer_name}</div>
            </div>
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t('deliveryOrdersModule.view.fields.challanDate')}</div>
              {editMode ? (
                <input
                  type="date"
                  value={challanDate}
                  onChange={e => setChallanDate(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 font-semibold text-gray-800 focus:outline-none focus:border-blue-400 transition-colors"
                  style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
                />
              ) : (
                <div className="px-4 py-3 rounded-xl border-2 font-semibold text-gray-800 bg-gray-50 border-gray-100">{formatDate(data.challan_date)}</div>
              )}
            </div>

            <div className="md:col-span-3 space-y-4">
              {/* Order Remark */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t('common.formFields.labels.orderRemark')}</div>
                {editMode ? (
                  <textarea
                    value={orderRemark}
                    onChange={e => setOrderRemark(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border text-sm font-medium resize-none focus:outline-none focus:border-yellow-400 transition-colors"
                    style={{ backgroundColor: '#fefce8', borderColor: '#fde68a', color: '#92400e' }}
                    placeholder={t('common.formFields.placeholders.addOrderRemark')}
                  />
                ) : data.order_remark ? (
                  <span className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-50 text-yellow-800 border border-yellow-200 shadow-sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {data.order_remark}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400 italic">{t('common.formFields.empty.noOrderRemark')}</span>
                )}
              </div>

              {/* Delivery Order Remark */}
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t('common.formFields.labels.deliveryOrderRemark')}</div>
                {editMode ? (
                  <textarea
                    value={remark}
                    onChange={e => setRemark(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border text-sm font-medium resize-none focus:outline-none focus:border-amber-400 transition-colors"
                    style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#b45309' }}
                    placeholder={t('common.formFields.placeholders.addDeliveryOrderRemark')}
                  />
                ) : data.remark ? (
                  <span className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-50 text-amber-800 border border-amber-200 shadow-sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {data.remark}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400 italic">{t('common.formFields.empty.noDeliveryOrderRemark')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <section>
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {t('deliveryOrdersModule.view.fields.additionalInformation')}
            </h3>
            <div className="grid grid-cols-2 gap-x-3 gap-y-4 md:grid-cols-4 p-4 rounded-xl border" style={{ backgroundColor: 'rgba(249,250,251,0.5)', borderColor: UI_COLORS.BORDERS.LIGHT }}>
              {editMode ? (
                <>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.vehicleNumber')}</span>
                    <input type="text" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.vehicleNumber')} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.driverContact')}</span>
                    <input type="text" value={driverContact} onChange={e => setDriverContact(e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.driverContact')} />
                  </div>
                  <InfoField label={t('common.formFields.labels.deliveryLocation')} value={data.location_name || data.delivery_location_name} />
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.emptyTruckWeightKgCapital')}</span>
                    <input type="number" value={emptyTruckWeight} onChange={e => setEmptyTruckWeight(e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.finalTruckWeightKgCapital')}</span>
                    <input type="number" value={finalTruckWeight} onChange={e => setFinalTruckWeight(e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                  <InfoField label={t('common.formFields.labels.calculatedWeight')} value={displayCalcWeight > 0 ? `${Math.ceil(displayCalcWeight)} Kg` : null} />
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.loadingCostPerKgShort')}</span>
                    <input type="number" value={loadingCostPerKg} onChange={e => setLoadingCostPerKg(e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                  <InfoField label={t('common.formFields.labels.freightType')} value={data.freight_type ? (FREIGHT_TYPE_LABELS[data.freight_type] || data.freight_type) : null} />
                  <InfoField label={t('common.formFields.labels.hsnCodes')} value={data.hsn_codes && data.hsn_codes.length > 0 ? data.hsn_codes.join(', ') : null} />
                  <SupervisorHistoryButton label={t('common.formFields.labels.supervisor')} supervisorName={data.supervisor_name} supervisorHistory={data.supervisor_history} />
                  <InfoField label={t('common.formFields.labels.generatedBy')} value={data.challan_generated_by_name} />
                  <InfoField label={t('common.formFields.labels.assignedBy')} value={data.assigned_by_name} />
                  <InfoField label={t('common.formFields.labels.orderCreatedBy')} value={data.do_created_by_name} />
                  <div className="flex flex-col col-span-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.vehicleRemark')}</span>
                    <input type="text" value={vehicleRemark} onChange={e => setVehicleRemark(e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.vehicleRemark')} />
                  </div>
                  <div className="flex flex-col col-span-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.remark')}</span>
                    <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2} className="px-2 py-1.5 rounded-lg border text-[13px] resize-none focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.remark')} />
                  </div>
                </>
              ) : (
                <>
                  <InfoField label={t('common.formFields.labels.vehicleNumber')} value={data.vehicle_number} />
                  <InfoField label={t('common.formFields.labels.driverContact')} value={data.driver_contact} />
                  <InfoField label={t('common.formFields.labels.deliveryLocation')} value={data.location_name || data.delivery_location_name} />
                  <InfoField label={t('common.formFields.labels.emptyTruckWeight')} value={data.empty_truck_weight ? `${Math.ceil(parseFloat(data.empty_truck_weight))} Kg` : null} />
                  <InfoField label={t('common.formFields.labels.finalTruckWeight')} value={data.final_truck_weight ? `${Math.ceil(parseFloat(data.final_truck_weight))} Kg` : null} />
                  <InfoField label={t('common.formFields.labels.calculatedWeight')} value={displayCalcWeight > 0 ? `${Math.ceil(displayCalcWeight)} Kg` : null} />
                  <InfoField label={t('common.formFields.labels.freightType')} value={data.freight_type ? (FREIGHT_TYPE_LABELS[data.freight_type] || data.freight_type) : null} />
                  <InfoField label={t('common.formFields.labels.hsnCodes')} value={data.hsn_codes && data.hsn_codes.length > 0 ? data.hsn_codes.join(', ') : null} />
                  <SupervisorHistoryButton label={t('common.formFields.labels.supervisor')} supervisorName={data.supervisor_name} supervisorHistory={data.supervisor_history} />
                  <InfoField label={t('common.formFields.labels.generatedBy')} value={data.challan_generated_by_name} />
                  <InfoField label={t('common.formFields.labels.assignedBy')} value={data.assigned_by_name} />
                  <InfoField label={t('common.formFields.labels.orderCreatedBy')} value={data.do_created_by_name} />
                </>
              )}
            </div>
          </section>

          {/* Items Section */}
          <section>
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faBoxOpen} />
              {t('deliveryOrdersModule.view.fields.items')}
            </h3>
            <div className="space-y-3">
              {editMode ? itemsUpdate.map((it, idx) => (
                <div key={it.id || idx} className="p-4 rounded-xl border-2 transition-all" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION, borderColor: UI_COLORS.BORDERS.MEDIUM }}>
                  <div className="text-sm font-bold text-gray-800 mb-3">
                    {it.size_name || it.product_name} <span className="text-gray-400 font-medium ml-1">({it.product_name || 'N/A'})</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.actualPieces')}</span>
                      <input type="number" value={it.actual_pieces} onChange={e => updateItem(idx, 'actual_pieces', e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.actualQtyWithUnit', { unit: it.unit })}</span>
                      <input type="number" value={it.actual_quantity} onChange={e => updateItem(idx, 'actual_quantity', e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.actualWeightKg')}</span>
                      <input type="number" value={it.actual_weight} onChange={e => updateItem(idx, 'actual_weight', e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.perPieceWeight')}</span>
                      <input type="number" value={it.per_piece_weight} onChange={e => updateItem(idx, 'per_piece_weight', e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.pricePerUnit', { unit: it.unit })}</span>
                      <input type="number" value={it.price} onChange={e => updateItem(idx, 'price', e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.remark')}</span>
                      <input type="text" value={it.remark} onChange={e => updateItem(idx, 'remark', e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-yellow-400 transition-colors" style={{ borderColor: '#fde68a', backgroundColor: '#fefce8' }} placeholder={t('common.formFields.placeholders.itemRemarkGeneric')} />
                    </div>

                    {it.coil_tracked && (
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {t('ordersModule.common.lengthNote')}
                        </span>
                        <input
                          type="text"
                          value={it.length_note || ''}
                          onChange={e => updateItem(idx, 'length_note', e.target.value)}
                          className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors"
                          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }}
                          placeholder={t('ordersModule.common.lengthNotePlaceholder')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )) : data.items?.map((it, idx) => (
                <div key={it.id || idx} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl border-2 transition-all hover:bg-gray-50" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SECTION, borderColor: UI_COLORS.BORDERS.LIGHT }}>
                  <div className="md:col-span-2">
                    <div className="text-sm font-bold text-gray-800">
                      {it.size_name || it.product_name} <span className="text-gray-400 font-medium ml-1">({it.product_name || 'N/A'})</span>
                    </div>
                    <div className="text-[10px] text-gray-400 font-bold uppercase mt-1">HSN: {it.hsn_code || 'N/A'} | GST: {it.gst_percentage}%</div>

                    <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-100">
                      <div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{t('deliveryOrdersModule.view.fields.loadingInfo')}</div>
                        <div className="text-xs font-semibold text-gray-600 mt-0.5">
                          {it.actual_pieces || 0} pieces @ ₹{it.price || 0}/{it.unit}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{t('deliveryOrdersModule.view.fields.metrics')}</div>
                        <div className="text-xs font-semibold text-gray-600 mt-0.5">
                          {it.unit?.toLowerCase() === 'm' ? `${Math.ceil(parseFloat(it.actual_quantity || 0))} m` : `${Math.ceil(parseFloat(it.actual_weight || 0))} kg`}
                          <span className="text-[10px] text-gray-400 ml-1">({Math.ceil(parseFloat(it.actual_weight || 0))} kg)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">{t('common.formFields.labels.remark')}</div>
                    {it.remark || it.order_item_remark ? (
                      <div className="flex items-start gap-1.5" style={{ borderLeft: '3px solid #f59e0b', paddingLeft: '8px' }}>
                        <span className="text-[11px] font-medium text-amber-800 break-words line-clamp-3">
                          {it.remark || it.order_item_remark}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-300 italic">-</span>
                    )}
                  </div>
                  <div className="text-right flex flex-col justify-center">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{t('deliveryOrdersModule.view.fields.item_amount')}</div>
                    <div className="text-lg font-bold text-gray-900">₹ {Number(it.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Summary Section */}
          <section className="flex justify-end pt-4">
            {editMode && (
              <div className="w-full md:w-1/2 bg-orange-50 border border-orange-100 p-5 rounded-2xl mb-4 mr-0 space-y-3">
                <p className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-2">{t('common.formFields.labels.editFinancialFields')}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.expenses')}</span>
                    <input type="number" value={expenses} onChange={e => setExpenses(e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.expenseRemark')}</span>
                    <input type="text" value={expenseRemark} onChange={e => setExpenseRemark(e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.expenseRemarkGeneric')} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.discount')}</span>
                    <input type="number" value={discount} onChange={e => setDiscount(e.target.value)} onWheel={e => e.target.blur()} className="csm-no-spin px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.discountRemark')}</span>
                    <input type="text" value={discountRemark} onChange={e => setDiscountRemark(e.target.value)} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder={t('common.formFields.placeholders.discountRemarkGeneric')} />
                  </div>
                  <div className="flex flex-col col-span-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.roundingOff')}</span>
                    <input type="number" value={roundingOff} onChange={e => { setRoundingOff(e.target.value); setRoundingTouched(true); }} className="px-2 py-1.5 rounded-lg border text-[13px] focus:outline-none focus:border-blue-400 transition-colors" style={{ borderColor: UI_COLORS.BORDERS.MEDIUM }} placeholder="0" />
                  </div>
                </div>
              </div>
            )}
            <div className="w-full md:w-1/2 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-3xl border border-blue-100 shadow-lg space-y-2">
              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>{t('deliveryOrdersModule.view.fields.subtotal')}</span>
                <span className="font-semibold text-gray-800">₹ {displaySubtotal.toFixed(2)}</span>
              </div>
              {(() => {
                const fType = data.freight_type || '';
                const freightAmt = Number(data.freight || 0);
                const isChargeable = fType === 'freight_chargeable';
                const isToPay = fType === 'free_delivery_freight_to_pay';
                const pricingType = data.freight_pricing_type || '';
                const typeLabel = isChargeable
                  ? `Freight Chargeable${pricingType === 'per_kg' ? ' (Per KG)' : pricingType === 'fixed' ? ' (Fixed)' : ''}`
                  : isToPay ? 'Freight To Pay'
                    : fType === 'ex_warehouse' ? 'Ex Warehouse'
                      : fType === 'free_delivery_by_supplier' ? 'Free by Supplier'
                        : fType === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
                          : t('deliveryOrdersModule.view.fields.freight');
                const color = isToPay ? 'text-green-600 font-bold' : isChargeable ? 'text-gray-500' : 'text-gray-400';
                const valueStr = isToPay
                  ? `- ₹ ${freightAmt.toFixed(2)}`
                  : isChargeable
                    ? `+ ₹ ${freightAmt.toFixed(2)}`
                    : `₹ ${freightAmt.toFixed(2)}`;
                return (
                  <div className={`flex justify-between items-center text-sm ${color}`}>
                    <span>{typeLabel}</span>
                    <span className="font-semibold">{valueStr}</span>
                  </div>
                );
              })()}
              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>{t('deliveryOrdersModule.view.fields.loading')}{editMode && loadingCostPerKg ? ` (${lt.loadingWeight.toFixed(2)} kg @ ₹${loadingCostPerKg}/kg)` : ''}</span>
                <span className="font-semibold text-gray-800">+ ₹ {displayLoading.toFixed(2)}</span>
              </div>

              {displayExpenses > 0 && (
                <div className="flex justify-between items-center text-sm text-orange-600 font-bold">
                  <span className="truncate mr-2" title={editMode ? expenseRemark : data.expense_remark}>{t('deliveryOrdersModule.view.fields.expenses')} {(editMode ? expenseRemark : data.expense_remark) ? `(${editMode ? expenseRemark : data.expense_remark})` : ''}</span>
                  <span>+ ₹ {displayExpenses.toFixed(2)}</span>
                </div>
              )}

              {displayDiscount > 0 && (
                <div className="flex justify-between items-center text-sm text-green-600 font-bold">
                  <span className="truncate mr-2" title={editMode ? discountRemark : data.discount_remark}>{t('deliveryOrdersModule.view.fields.discount')} {(editMode ? discountRemark : data.discount_remark) ? `(${editMode ? discountRemark : data.discount_remark})` : ''}</span>
                  <span>- ₹ {displayDiscount.toFixed(2)}</span>
                </div>
              )}

              <div className="pt-2 border-t border-blue-200 flex justify-between items-center text-sm font-bold text-blue-800">
                <span>{t('common.formFields.labels.totalWithoutGst')}</span>
                <span>₹ {displayTotalWithoutGst.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>GST @ 18%</span>
                <span className="font-semibold text-gray-800">+ ₹ {displayCombinedGst.toFixed(2)}</span>
              </div>

              <div className="pt-4 border-t border-blue-200 flex justify-between items-center text-sm font-bold text-blue-800">
                <span>Total (inc. GST)</span>
                <span>₹ {displayTotalWithGst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              {displayRoundingOff !== 0 && (
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>{t('common.formFields.labels.roundingOff')}</span>
                  <span className="font-semibold text-gray-700">₹ {displayRoundingOff.toFixed(2)}</span>
                </div>
              )}

              <div className="pt-3 border-t-2 border-blue-400 flex justify-between items-end">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter leading-none mb-1">{t('deliveryOrdersModule.view.fields.totalWeight')}</p>
                  <p className="text-lg font-bold text-gray-700">{Math.ceil(parseFloat(data.total_delivered_quantity || data.total_weight || data.total_quantity || 0))} KG</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">{t('deliveryOrdersModule.view.fields.totalPayable')}</p>
                  <h4 className="text-3xl font-black text-blue-900 tracking-tighter">
                    ₹ {Math.round(displayFinalTotal).toLocaleString(undefined)}
                  </h4>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t shrink-0 bg-gray-50" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          {editMode ? (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={saveLoading}
                className="px-6 py-3 rounded-2xl font-black transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
                style={{ backgroundColor: 'transparent', color: UI_COLORS.TEXT.MUTED, border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="px-8 py-3 rounded-2xl font-black shadow-xl transition-all hover:shadow-2xl hover:-translate-y-0.5 active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
              >
                {saveLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    <span>{t('common.formFields.labels.saveChanges')}</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {onSendNotification && emailEnabled && (
                <button
                  onClick={() => handleSendNotification('email')}
                  disabled={emailLoading}
                  className="px-6 py-3 rounded-2xl font-black shadow transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center gap-2"
                  style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1), color: UI_COLORS.STATUS.INFO, border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.3)}` }}
                  title={t('common.actions.sendEmail')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  {emailLoading ? t('deliveryOrdersModule.common.buttons.sendingEmail') : 'Send Email'}
                </button>
              )}
              {onSendNotification && whatsappEnabled && (
                <button
                  onClick={() => handleSendNotification('whatsapp')}
                  disabled={emailLoading}
                  className="px-6 py-3 rounded-2xl font-black shadow transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center gap-2"
                  style={{ backgroundColor: '#25D366', color: '#ffffff', border: '1px solid #25D366' }}
                  title={t('common.actions.sendWhatsApp')}
                >
                  <FontAwesomeIcon icon={faComment} />
                  {emailLoading ? 'Sending...' : 'Send WhatsApp'}
                </button>
              )}
              <button
                onClick={onClose}
                className="px-10 py-3 rounded-2xl font-black shadow-xl transition-all hover:shadow-2xl hover:-translate-y-0.5 active:scale-95 flex items-center gap-3"
                style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
              >
                <span>{t('deliveryOrdersModule.common.buttons.close')}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallanSuccessModal;
